import React, { useCallback, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ShouldRender } from '@/components/ShouldRender';
import { FormItem } from '@/components/ui/form-item';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { GroupedNotifications, NotificationItem } from '@/types/User/UserNotificationData';
import { Row } from '@tanstack/react-table';
import { Info } from 'lucide-react';

import { UserNotificationConfigOperationModal } from './user-notification-config-operation.modal';
import { NotificationFormValues } from './user-notification-config.form';

type NotificationConfigFormColumnsProps = {
  notifications: NotificationItem[];
  messages: NotificationFormValues['messages'];
  form: UseFormReturn<NotificationFormValues>;
  userId: string;
};

export interface NotificationRowData extends NotificationItem {
  isGroupHeader?: boolean;
  groupType?: string;
  allSelected?: boolean;
  isSelected?: boolean;
  subRows?: NotificationRowData[];
}

export const userNotificationConfigFormConfig = ({
  notifications,
  messages,
  form,
  userId,
}: NotificationConfigFormColumnsProps) => {
  const { t, i18n } = useTranslation();

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const groupNotificationsByType = useCallback((notifications: NotificationItem[]) => {
    if (!notifications) return {};

    return notifications.reduce((acc: GroupedNotifications, notification, index) => {
      if (!notification?.message_type?.name) return acc;

      const type = notification.message_type.name;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push({ ...notification, index });
      return acc;
    }, {});
  }, []);

  const formMessages = form.watch('messages');

  const groupedNotifications = useMemo(() => {
    return groupNotificationsByType(notifications || []);
  }, [notifications]);

  const toggleSection = (type: string) => {
    setExpanded(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const toggleAllSections = () => {
    const newExpandedState = !Object.values(expanded).some(Boolean);
    const groupedData = groupNotificationsByType(notifications || []);

    const newExpanded: Record<string, boolean> = {};
    Object.keys(groupedData).forEach(type => {
      newExpanded[type] = newExpandedState;
    });

    setExpanded(newExpanded);
  };

  const toggleAllNotifications = (
    type: string,
    notificationType: 'via_email' | 'via_broadcast' | 'via_teams',
    checked: boolean,
  ) => {
    const notificationsOfType = groupedNotifications[type];
    notificationsOfType.forEach(notification => {
      form.setValue(`messages[${notification.id}].${notificationType}`, checked);
    });
  };

  const checkNotificationIsSelected = (
    notification: NotificationItem,
    type: 'via_email' | 'via_broadcast' | 'via_teams',
  ) => {
    const formInput = formMessages[notification.id];
    if (!notification.id || !formInput) return false;
    return formInput[type];
  };

  const checkIsAllGroupedNotificationsSelected = (
    type: string,
    notificationType: 'via_email' | 'via_broadcast' | 'via_teams',
  ) => {
    if (!type) return false;
    const notificationsOfType = groupedNotifications[type];
    return notificationsOfType.every(notification => checkNotificationIsSelected(notification, notificationType));
  };

  const columns = useMemo(
    () => [
      {
        id: 'name',
        header: () => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex h-6 w-6 min-w-6 items-center justify-center"
              onClick={toggleAllSections}
            >
              {Object.values(expanded).some(Boolean) ? '+' : '-'}
            </button>
            <span>{t('notification-title')}</span>
          </div>
        ),
        meta: {
          body: {
            cell: {
              className: 'p-0 m-0',
            },
          },
        },
        cell: ({ row }: { row: Row<NotificationRowData> }) => {
          const data = row.original;

          if (data.isGroupHeader) {
            return (
              <div className="relative flex h-full w-full items-center gap-2 bg-gray-50 p-4">
                <div className="absolute inset-0" />
                <div className="relative z-10 flex w-full items-center gap-2">
                  <button
                    type="button"
                    className="flex h-6 w-6 min-w-6 items-center justify-center"
                    onClick={() => toggleSection(data.groupType!)}
                  >
                    {expanded[data.groupType!] ? '+' : '-'}
                  </button>
                  <div className="flex items-center gap-2 font-bold">{t(`${data.groupType}`)}</div>
                </div>
              </div>
            );
          }

          const isChangeStatusNotification = data.name['en' as keyof typeof data.name]
            ?.toLowerCase()
            .includes('status');

          return (
            <div className="flex w-full items-center justify-between gap-2 whitespace-nowrap pl-8 pr-4">
              <span className="flex items-center gap-2">
                {data.name[i18n.language as keyof typeof data.name]}
                <ShouldRender condition={!!data.description}>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info size={16} className="text-blue-500" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[300px]">
                      <p className="text-wrap">
                        {data.description?.[i18n.language as keyof typeof data.description] ?? t('not-provided')}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </ShouldRender>
              </span>

              <div className="flex items-center gap-2">
                <ShouldRender
                  condition={
                    isChangeStatusNotification && data.message_type_id === 'd7126edd-3c20-4a27-8ebd-7c6fcb0c824b'
                  }
                >
                  <UserNotificationConfigOperationModal message_id={data.id} userId={userId} />
                </ShouldRender>
              </div>
            </div>
          );
        },
      },
      {
        id: 'via_email',
        header: t('send-email'),
        meta: {
          body: {
            cell: {
              className: 'p-0 m-0',
            },
          },
        },
        cell: ({ row }: { row: Row<NotificationRowData> }) => {
          const data = row.original;
          const isChecked = checkIsAllGroupedNotificationsSelected(data.groupType ?? '', 'via_email');
          if (data.isGroupHeader) {
            return (
              <CheckAllSwitch
                isChecked={isChecked}
                onChange={toggleAllNotifications}
                groupType={data.groupType ?? ''}
                type="via_email"
              />
            );
          }

          return (
            <div className="p-2 text-center">
              <FormItem name={`messages[${data.id}].via_email`}>
                <Switch checked={form.watch(`messages[${data.id}].via_email`)} />
              </FormItem>
            </div>
          );
        },
      },
      {
        id: 'via_broadcast',
        header: t('send-on-site'),
        meta: {
          body: {
            cell: {
              className: 'p-0 m-0',
            },
          },
        },
        cell: ({ row }: { row: Row<NotificationRowData> }) => {
          const data = row.original;
          const isChecked = checkIsAllGroupedNotificationsSelected(data.groupType ?? '', 'via_broadcast');
          if (data.isGroupHeader) {
            return (
              <CheckAllSwitch
                isChecked={isChecked}
                onChange={toggleAllNotifications}
                groupType={data.groupType ?? ''}
                type="via_broadcast"
              />
            );
          }

          return (
            <div className="p-2 text-center">
              <FormItem name={`messages[${data.id}].via_broadcast`}>
                <Switch checked={form.watch(`messages[${data.id}].via_broadcast`)} />
              </FormItem>
            </div>
          );
        },
      },
      {
        id: 'via_teams',
        header: t('send-to-teams'),
        meta: {
          body: {
            cell: {
              className: 'p-0 m-0',
            },
          },
        },
        cell: ({ row }: { row: Row<NotificationRowData> }) => {
          const data = row.original;
          const isChecked = checkIsAllGroupedNotificationsSelected(data.groupType ?? '', 'via_teams');
          if (data.isGroupHeader) {
            return (
              <CheckAllSwitch
                isChecked={isChecked}
                onChange={toggleAllNotifications}
                groupType={data.groupType ?? ''}
                type="via_teams"
              />
            );
          }

          return (
            <div className="p-2 text-center">
              <FormItem name={`messages[${data.id}].via_teams`}>
                <Switch checked={form.watch(`messages[${data.id}].via_teams`)} />
              </FormItem>
            </div>
          );
        },
      },
    ],
    [messages, form.formState.dirtyFields, expanded, notifications],
  );

  return { columns, expanded, groupedNotifications };
};

const CheckAllSwitch = ({
  isChecked,
  onChange,
  groupType,
  type,
}: {
  isChecked: boolean;
  onChange: (groupType: string, type: 'via_email' | 'via_broadcast' | 'via_teams', checked: boolean) => void;
  groupType: string;
  type: 'via_email' | 'via_broadcast' | 'via_teams';
}) => {
  return (
    <div className="relative flex h-full items-center gap-2 whitespace-nowrap bg-gray-50 p-2">
      <Switch onChange={() => onChange(groupType, type, !isChecked)} checked={isChecked} />
    </div>
  );
};
